/**
 * Date order as a REGIONAL SETTING, resolved through the preference cascade.
 *
 * Joel's spec names exactly two orders, month-first and day-first, so that is the
 * type. But the stored value is a free-form string in the preferences document and
 * always has been — the company tier has carried `dateFormat` since before this
 * feature, filled by hand and never validated. So the union is the RESOLVED shape,
 * never the parsed one: everything entering the app goes through normalizeDateFormat.
 */
export type DateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY';

/** Month-first, matching the app's historical hardcoded rendering. */
export const DEFAULT_DATE_FORMAT: DateFormat = 'MM/DD/YYYY';

/**
 * Coerce a stored value to one of the two supported orders.
 *
 * Deliberately permissive: it accepts the .NET-style patterns the desktop and the
 * existing company documents use (`dd/MM/yyyy`, `d.M.yyyy`, `yyyy-MM-dd`) as well as
 * the two canonical strings, because the alternative — trusting the stored text —
 * puts an unrenderable pattern in front of every date in the app. The rule is
 * ORDER ONLY: whether the day or the month comes first. Separator and padding are
 * the renderer's business, not the preference's.
 *
 * ISO (`yyyy-MM-dd`) is year-first and belongs to neither, so it resolves to the
 * default rather than being silently treated as day-first.
 */
export function normalizeDateFormat(raw: string | null | undefined): DateFormat {
  const value = (raw ?? '').trim();
  if (!value) return DEFAULT_DATE_FORMAT;

  const upper = value.toUpperCase();
  if (upper === 'MM/DD/YYYY') return 'MM/DD/YYYY';
  if (upper === 'DD/MM/YYYY') return 'DD/MM/YYYY';

  // Find which of D or M appears first among the date tokens, ignoring the year
  // so `yyyy-dd-MM` cannot be read as day-first on the strength of its year.
  const withoutYear = upper.replace(/Y+/g, '');
  const day = withoutYear.indexOf('D');
  const month = withoutYear.indexOf('M');
  if (day < 0 || month < 0) return DEFAULT_DATE_FORMAT;
  return day < month ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
}

/** True when the resolved order puts the day first. */
export function isDayFirst(format: DateFormat): boolean {
  return format === 'DD/MM/YYYY';
}

/** How much of the clock to show beside the date. */
export type TimePrecision = 'none' | 'minutes' | 'seconds';

/**
 * Clock convention — the third regional setting, beside date order and units.
 *
 * Same shape as DateFormat and for the same reason: the union is the RESOLVED
 * value, never the parsed one, because what arrives from the preferences document
 * is a free-form string.
 */
export type TimeFormat = '24h' | '12h';

/** 24-hour, which is what every clock in this app rendered before the setting. */
export const DEFAULT_TIME_FORMAT: TimeFormat = '24h';

/**
 * Coerce a stored value to one of the two conventions.
 *
 * Permissive in the same way normalizeDateFormat is: it accepts the patterns a
 * hand-filled document is likely to hold — "HH:mm" and "H" mean 24-hour, "h:mm
 * a" and "hh" mean 12-hour — as well as the canonical strings. A lower-case 'h'
 * is the 12-hour marker in every .NET and Unicode pattern; an upper-case 'H' is
 * 24-hour. Anything unreadable falls back to 24-hour, which is what the app did
 * before this existed.
 */
export function normalizeTimeFormat(raw: string | null | undefined): TimeFormat {
  const value = (raw ?? '').trim();
  if (!value) return DEFAULT_TIME_FORMAT;

  const lower = value.toLowerCase();
  if (lower === '24h' || lower === '24' || lower === '24-hour') return '24h';
  if (lower === '12h' || lower === '12' || lower === '12-hour') return '12h';

  // A PATTERN, not prose. This must be checked before reading h/H out of the
  // string, because ordinary words contain those letters — "whenever" has an 'h'
  // and was being read as a 12-hour pattern. Only a string made entirely of
  // pattern characters is treated as one.
  //
  // Case matters here and nowhere else in this function, so the test runs against
  // the ORIGINAL string rather than the lower-cased copy: lower 'h' is the
  // 12-hour marker in every .NET and Unicode pattern, upper 'H' is 24-hour.
  // "Looks like a time pattern" means it has a CLOCK SEPARATOR, or is just the
  // hour marker on its own. Testing for pattern CHARACTERS alone is not enough:
  // widen the character class far enough to accept "h:mm a" and ordinary words
  // like "has" start matching it too. A colon is what every real time pattern
  // has and no English word does.
  const looksLikePattern = /:/.test(value) || /^[hH]{1,2}$/.test(value);
  if (looksLikePattern) {
    if (/H/.test(value)) return '24h';
    if (/h/.test(value)) return '12h';
  }
  return DEFAULT_TIME_FORMAT;
}

/** True when the resolved convention uses a 12-hour clock with AM/PM. */
export function isTwelveHour(format: TimeFormat): boolean {
  return format === '12h';
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

/**
 * Render a date in the user's chosen ORDER.
 *
 * Deliberately not `toLocaleDateString()`, which was what the project grid used:
 * that follows the BROWSER's locale, so the same account saw a different order on
 * a work laptop and a home one, and the preference could not influence it at all.
 * The order here comes from the cascade and nothing else.
 *
 * Time is 24-hour. The app already rendered 24-hour clocks in its logs and tables,
 * and a 12/24-hour toggle is a separate preference nobody has asked for — mixing
 * one in here would be inventing scope.
 */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  format: DateFormat,
  precision: TimePrecision = 'none',
  timeFormat: TimeFormat = DEFAULT_TIME_FORMAT
): string {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  // An unparseable date must not render as "NaN/NaN/NaN". Empty is the same thing
  // the grid already shows for a project with no telemetry.
  if (Number.isNaN(date.getTime())) return '';

  const d = pad(date.getDate());
  const m = pad(date.getMonth() + 1);
  const y = String(date.getFullYear());
  const datePart = isDayFirst(format) ? `${d}/${m}/${y}` : `${m}/${d}/${y}`;
  if (precision === 'none') return datePart;

  return `${datePart} ${formatTime(date, timeFormat, precision)}`;
}

/**
 * The clock part, on its own.
 *
 * Separate from formatDateTime because plenty of surfaces show a time without a
 * date — a tick label, a live clock — and they must use the same convention as
 * the ones that show both.
 *
 * MIDNIGHT AND NOON ARE THE CASES THAT GO WRONG. In 12-hour form hour 0 is
 * "12 AM" and hour 12 is "12 PM"; a naive `hours % 12` renders both as "0",
 * which is a time no clock shows.
 */
export function formatTime(
  value: Date | string | number | null | undefined,
  timeFormat: TimeFormat = DEFAULT_TIME_FORMAT,
  precision: Exclude<TimePrecision, 'none'> = 'minutes'
): string {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const seconds = precision === 'seconds' ? `:${pad(date.getSeconds())}` : '';
  const minutes = pad(date.getMinutes());

  if (timeFormat === '12h') {
    const raw = date.getHours();
    const hour12 = raw % 12 === 0 ? 12 : raw % 12;
    const meridiem = raw < 12 ? 'AM' : 'PM';
    // NOT zero-padded: "1:03 PM", never "01:03 PM", which no 12-hour clock uses.
    return `${hour12}:${minutes}${seconds} ${meridiem}`;
  }
  return `${pad(date.getHours())}:${minutes}${seconds}`;
}
